import type { FC } from "react";

import type { ProductGalleryAsset } from "@jti/fe-common";
import { getOptimizedMagentoImageSrcSet } from "@jti/ui/utils/getOptimizedMagentoImageSrcSet";

const GALLERY_IMAGE_WIDTHS = [375, 430, 768] as const;
const GALLERY_IMAGE_BASE_WIDTH = 375;
const GALLERY_IMAGE_QUALITY = 80;
const GALLERY_IMAGE_SIZES = "(max-width: 430px) 100vw, (max-width: 768px) 430px, 768px";

const getGalleryImageSrcSet = (src: string, format?: "avif" | "webp") =>
  getOptimizedMagentoImageSrcSet({
    src,
    baseWidth: GALLERY_IMAGE_BASE_WIDTH,
    descriptor: "width",
    widths: GALLERY_IMAGE_WIDTHS,
    quality: GALLERY_IMAGE_QUALITY,
    format,
  });

export const ProductGalleryImage: FC<{ item: ProductGalleryAsset; idx?: number }> = ({ item, idx = 0 }) => {
  const avifImage = getGalleryImageSrcSet(item.path, "avif");
  const webpImage = getGalleryImageSrcSet(item.path, "webp");
  const fallbackImage = getGalleryImageSrcSet(item.path);

  // The first gallery image is part of the initial viewport, while the rest of
  // the carousel can stay lazy without changing the interaction model.
  const isPriorityImage = idx === 0;

  return (
    <picture className="carousel__galleryPicture">
      {/* Prefer the most efficient format first and keep a plain img fallback for compatibility. */}
      <source srcSet={avifImage.optimizedImageSrcSet} type="image/avif" sizes={GALLERY_IMAGE_SIZES} />
      <source srcSet={webpImage.optimizedImageSrcSet} type="image/webp" sizes={GALLERY_IMAGE_SIZES} />
      <img
        src={fallbackImage.optimizedImageSrc1x || item.path}
        srcSet={fallbackImage.optimizedImageSrcSet}
        sizes={GALLERY_IMAGE_SIZES}
        alt={item.label ?? ""}
        className="carousel__galleryImg"
        data-testid={`carousel__gallery-image-${idx}`}
        loading={isPriorityImage ? "eager" : "lazy"}
        decoding="async"
        {...{ fetchPriority: isPriorityImage ? "high" : "auto" }}
      />
    </picture>
  );
};
